/**
 * `useTranslation()` — the hook every component uses for copy.
 *
 * It subscribes to the language store via `useSyncExternalStore`, so flipping the language in
 * Settings re-renders every mounted component with the new strings — no reload. `t` and `plural`
 * are bound to the current language; `lang` is exposed for the rare component that needs the raw
 * value (e.g. to pick a `date-fns` locale via `dateLocale.ts`).
 *
 * For non-React code (outbox, mutation hooks firing outside the tree) use the standalone
 * `translate()` from `t.ts` instead — it reads the same store without subscribing.
 */
import { useCallback, useMemo } from 'react';
import { useSyncExternalStore } from 'react';
import { read, subscribe, type Language } from './store';
import { translateIn, translatePlural, type TParams } from './t';
import type { MessageKey } from './messages';

export interface Translation {
  t: (key: MessageKey, params?: TParams) => string;
  plural: (base: string, count: number, params?: TParams) => string;
  lang: Language;
}

export function useTranslation(): Translation {
  // Server snapshot is `'en'`: there is no SSR here, but `useSyncExternalStore` requires one and
  // English is the correct pre-hydration default.
  const lang = useSyncExternalStore(subscribe, read, () => 'en' as Language);

  const t = useCallback(
    (key: MessageKey, params?: TParams) => translateIn(lang, key, params),
    [lang],
  );

  const plural = useCallback(
    (base: string, count: number, params?: TParams) => translatePlural(lang, base, count, params),
    [lang],
  );

  return useMemo(() => ({ t, plural, lang }), [t, plural, lang]);
}
